import React from 'react';
import classNames from 'classnames';

import Data, { _F } from 'data';
import { ItemIcon, WithItemTooltip, WithEngravingTooltip } from 'tooltip';
import { getIconPath } from 'common';

import './Paperdoll.scss';
import Tooltip from 'ui/tooltip';
import { formatHtml } from 'utils/format';

function ItemSlot({ slot, item, selected, onSelect }) {
  const data = item && Data.items[item.id];
  const onClick = React.useCallback(() => onSelect?.(prev => prev === slot ? null : slot), [slot, onSelect]);
  if (!data) return <div className={classNames(`lap-ItemSlot lap-slot-${slot} lap-empty`, {"lap-selected": selected === slot})} onClick={onClick}/>;
  return (
    <WithItemTooltip item={item}>
      <div className={classNames(`lap-ItemSlot lap-slot-${slot}`, {"lap-selected": selected === slot})} onClick={onClick}>
        {!!data && (
          <div className={`lap-item-icon lap-item-background lap-item-${Data.itemGrades[data.grade || 0].color}`}>
            <ItemIcon item={data} upgrade={item.upgrade}/>
          </div>
        )}
      </div>
    </WithItemTooltip>
  );
}

function EngravingSlot({ value, selected, onClick }) {
  const data = Data.engraving[value?.id];
  if (!data) return (
    <div className={classNames(`lap-ItemSlot lap-slot-engraving lap-empty`, {"lap-selected": selected})} onClick={onClick}>
      <div className="lap-engraving-icon lap-empty">
        <div className="lap-engraving-frame"/>
      </div>
    </div>
  );
  return (
    <WithEngravingTooltip id={value.id} points={value?.points}>
      <div className={classNames(`lap-ItemSlot lap-slot-engraving`, {"lap-selected": selected})} onClick={onClick}>
        <div className="lap-engraving-icon" style={{backgroundImage: `url(${getIconPath(data.icon)})`}}>
          <div className="lap-engraving-frame"/>
        </div>
      </div>
    </WithEngravingTooltip>
  );
}

const leftSlots = ['head', 'shoulders', 'torso', 'legs', 'hands', 'weapon'];
const rightSlots = ['necklace', 'earring1', 'earring2', 'ring1', 'ring2', 'bracelet', 'stone'];

const statCoeff = {
  criticalhit: 0.2794,
  specialty: 0.0699,
  oppression: 0.163,
  rapidity: 0.5821,
  endurance: 0.0978,
  mastery: 0.2329,
};

function statTipText(id, charClass) {
  switch (id) {
  case 'criticalhit': return ['tip.desc.enum_stattype_criticalhit', 74];
  case 'specialty':
    switch (charClass) {
    case 'berserker': return ['tip.desc.enum_stattype_specialty_berserker', 107, 108, 109, 110];
    case 'destroyer': return ['tip.desc.enum_stattype_specialty_destroyer', 107, 108, 109, 110];
    case 'warlord': return ['tip.desc.enum_stattype_specialty_warlord', 107, 108, 109, 110];
    case 'holyknight': return ['tip.desc.enum_stattype_specialty_holyknight', 107, 108, 110];
    case 'arcana': return ['tip.desc.enum_stattype_specialty_arcana', 107, 108, 110];
    case 'summoner': return ['tip.desc.enum_stattype_specialty_summoner', 107, 108, 110];
    case 'bard': return ['tip.desc.enum_stattype_specialty_bard', 107, 108, 109, 110];
    case 'elementalmaster': return ['tip.desc.enum_stattype_specialty_elemental_master', 107, 108, 110];
    case 'battlemaster': return ['tip.desc.enum_stattype_specialty_battle_master', 107, 108, 110];
    case 'infighter': return ['tip.desc.enum_stattype_specialty_infighter', 107, 108, 110];
    case 'forcemaster': return ['tip.desc.enum_stattype_specialty_force_master', 107, 108, 110];
    case 'lancemaster': return ['tip.desc.enum_stattype_specialty_lance_master', 107, 108, 109, 110];
    case 'battlemaster_male': return ['tip.desc.enum_stattype_specialty_battle_master_male', 107, 108, 110];
    case 'blade': return ['tip.desc.enum_stattype_specialty_blade', 107, 108, 109, 110];
    case 'demonic': return ['tip.desc.enum_stattype_specialty_demonic', 107, 108, 109, 110];
    case 'reaper': return ['tip.desc.enum_stattype_specialty_reaper', 107, 108, 110];
    case 'hawk_eye': return ['tip.desc.enum_stattype_specialty_hawk_eye', 107, 108, 110];
    case 'devilhunter': return ['tip.desc.enum_stattype_specialty_devil_hunter', 107, 108, 109, 110];
    case 'blaster': return ['tip.desc.enum_stattype_specialty_blaster', 107, 108, 110];
    case 'scouter': return ['tip.desc.enum_stattype_specialty_scouter', 107, 108, 110];
    case 'devilhunter_female': return ['tip.desc.enum_stattype_specialty_devil_hunter_female', 107, 108, 109, 110];
    default: return ['tip.desc.enum_stattype_specialty_basic_class'];
    }
  case 'oppression': return ['tip.desc.enum_stattype_oppression', 17, 18];
  case 'rapidity': return ['tip.desc.enum_stattype_rapidity', 80, 78, 53];
  case 'endurance': return ['tip.desc.enum_stattype_endurance', 59, 60, 71, 41];
  case 'mastery': return ['tip.desc.enum_stattype_mastery', 106, 105, 54];
  default: return null;
  }
}

function statTipTextOther(id, charClass) {
  switch (id) {
  case 'criticalhit': return ['tip.desc.enum_stattype_criticalhit_other', 74];
  case 'specialty':
    switch (charClass) {
    case 'berserker': return ['tip.desc.enum_stattype_specialty_berserker_other', 107, 108, 109, 110];
    case 'destroyer': return ['tip.desc.enum_stattype_specialty_destroyer_other', 107, 108, 109, 110];
    case 'warlord': return ['tip.desc.enum_stattype_specialty_warlord_other', 107, 108, 109, 110];
    case 'holyknight': return ['tip.desc.enum_stattype_specialty_holyknight_other', 107, 108, 110];
    case 'arcana': return ['tip.desc.enum_stattype_specialty_arcana_other', 107, 108, 110];
    case 'summoner': return ['tip.desc.enum_stattype_specialty_summoner_other', 107, 108, 110];
    case 'bard': return ['tip.desc.enum_stattype_specialty_bard_other', 107, 108, 109, 110];
    case 'elementalmaster': return ['tip.desc.enum_stattype_specialty_elemental_master_other', 107, 108, 110];
    case 'battlemaster': return ['tip.desc.enum_stattype_specialty_battle_master_other', 107, 108, 110];
    case 'infighter': return ['tip.desc.enum_stattype_specialty_infighter_other', 107, 108, 110];
    case 'forcemaster': return ['tip.desc.enum_stattype_specialty_force_master_other', 107, 108, 110];
    case 'lancemaster': return ['tip.desc.enum_stattype_specialty_lance_master_other', 107, 108, 109, 110];
    case 'battlemaster_male': return ['tip.desc.enum_stattype_specialty_battle_master_male_other', 107, 108, 110];
    case 'blade': return ['tip.desc.enum_stattype_specialty_blade_other', 107, 108, 109, 110];
    case 'demonic': return ['tip.desc.enum_stattype_specialty_demonic_other', 107, 108, 109, 110];
    case 'reaper': return ['tip.desc.enum_stattype_specialty_reaper_other', 107, 108, 110];
    case 'hawk_eye': return ['tip.desc.enum_stattype_specialty_hawk_eye_other', 107, 108, 110];
    case 'devilhunter': return ['tip.desc.enum_stattype_specialty_devil_hunter_other', 107, 108, 109, 110];
    case 'blaster': return ['tip.desc.enum_stattype_specialty_blaster_other', 107, 108, 110];
    case 'scouter': return ['tip.desc.enum_stattype_specialty_scouter_other', 107, 108, 110];
    case 'devilhunter_female': return ['tip.desc.enum_stattype_specialty_devil_hunter_female_other', 107, 108, 109, 110];
    case 'yinyangshi': return ['tip.desc.enum_stattype_specialty_yinyangshi_other', 107, 108, 109, 110];
    case 'weatherartist': return ['tip.desc.enum_stattype_specialty_weather_artist_other', 107, 108, 110];
    case 'berserker_female': return ['tip.desc.enum_stattype_specialty_berserker_female_other', 107, 108, 109, 110];
    default: return ['tip.desc.enum_stattype_specialty_basic_class_other'];
    }
  case 'oppression': return ['tip.desc.enum_stattype_oppression_other', 17, 18];
  case 'rapidity': return ['tip.desc.enum_stattype_rapidity_other', 80, 78, 53];
  case 'endurance': return ['tip.desc.enum_stattype_endurance_other', 59, 60, 71, 41];
  case 'mastery': return ['tip.desc.enum_stattype_mastery_other', 106, 105, 54];
  default: return null;
  }
}

export function StatTip({ id, value, books, potions, charClass }) {
  const base = value / (statCoeff[id] || 1);
  const target = {};
  for (const row of [...Data.statConversion, {base: 17, target: 17, rate: 1}, {base: 17, target: 18, rate: 1.15}]) {
    if (row.class && Data.classMap[row.class] !== charClass) continue;
    if (Data.statsMap[row.base] === id) target[row.target] = base * row.rate;
  }
  const text = (books || potions ? statTipText(id, charClass) : statTipTextOther(id, charClass));
  if (!text || !Data.strings[text[0]]) return null;
  const args = text.slice(1).map(id => <span style={{color: '#8f8'}}>{((target[id] || 0) / 100).toFixed(2) + '%'}</span>);
  args[9] = <span style={{color: '#8f8'}}>{potions || 0}</span>;
  args[10] = <span style={{color: '#8f8'}}>{books || 0}</span>;
  return (
    <div className="lap-StatTip">
      {formatHtml(Data.strings[text[0]], ...args)}
    </div>
  );
}

export default function Paperdoll({ embed, selected, onSelect, noStats }) {
  const stats = React.useMemo(() => {
    const stats = {};
    for (const item of Object.values(embed.items)) {
      if (!item?.quality) continue;
      for (const [key, value] of Object.entries(item.quality)) {
        stats[key] = (stats[key] || 0) + value;
      }
    }
    return ['criticalhit', 'specialty', 'oppression', 'rapidity', 'endurance', 'mastery'].filter(stat => stats[stat]).map(stat => [stat, stats[stat]]);
  }, [embed.items]);
  const onSelectEngraving1 = React.useCallback(() => onSelect?.(prev => prev === 'engraving1' ? null : 'engraving1'), [onSelect]);
  const onSelectEngraving2 = React.useCallback(() => onSelect?.(prev => prev === 'engraving2' ? null : 'engraving2'), [onSelect]);
  const noEngravings = !onSelect && (!embed.engraving1 && !embed.engraving2);
  return (
    <div className={classNames("lap-Paperdoll", {"lap-no-engraving": noEngravings})}>
      <div className={`lap-PaperdollBackground lap-class-${embed.class}`}/>
      {!noStats && (
        <div className="lap-PaperdollStats">
          <div className="lap-title">
            <span className={`lap-class-icon lap-icon-medium lap-class-${embed.class}`}/>
            {_F(Data.playerClasses[embed.class].name)}
          </div>
          {stats.map(([key, value]) => (
            <Tooltip key={key} tooltip={<StatTip id={key} value={value} charClass={embed.class}/>}>
              <div><span>{_F(Data.itemStats[key].tip)}</span><span>{value}</span></div>
            </Tooltip>
          ))}
        </div>
      )}
      <div className="lap-left-slots">
        {leftSlots.map(slot => <ItemSlot slot={slot} item={embed.items[slot]} selected={selected} onSelect={onSelect} key={slot}/>)}
        <EngravingSlot value={embed.engraving1} selected={selected === 'engraving1'} onClick={onSelectEngraving1}/>
        <EngravingSlot value={embed.engraving2} selected={selected === 'engraving2'} onClick={onSelectEngraving2}/>
      </div>
      <div className="lap-right-slots">
        {rightSlots.map(slot => <ItemSlot slot={slot} item={embed.items[slot]} selected={selected} onSelect={onSelect} key={slot}/>)}
      </div>
    </div>
  );
}
